import { createContext, use, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { LoginRequest, RegisterRequest, User } from '@sf/api';
import { useApi } from '../api/ApiProvider';
import { readSession } from './readSession';

export type AuthStatus = 'loading' | 'authenticated' | 'anonymous';

export interface AuthContextValue {
  status: AuthStatus;
  user: User | null;
  login: (body: LoginRequest) => Promise<User>;
  register: (body: RegisterRequest) => Promise<User>;
  /** Opens the shared, read-only demo account for a language (no password — see src/demo). */
  demo: (lang: string) => Promise<User>;
  logout: () => Promise<void>;
  /** Re-reads GET /me (after profile changes or e-mail verification). */
  reload: () => Promise<User | null>;
  /** Replaces the cached user without a round-trip (e.g. after PATCH /me). */
  setUser: (user: User) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Owns the signed-in customer. Tokens themselves live in the API client (localStorage
 * `sf.customer.tokens`) which also refreshes them on 401; this provider only mirrors the
 * resulting user state so route guards and the nav bar can react.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [user, setUserState] = useState<User | null>(null);
  const [status, setStatus] = useState<AuthStatus>(api.isAuthenticated() ? 'loading' : 'anonymous');
  const statusRef = useRef(status);
  statusRef.current = status;

  const loadMe = useCallback(async () => {
    // What a failed read means is readSession's decision, and it never touches the token store: a request
    // that never reached the server is not an answer, and never ends a session.
    const { status: next, user: me } = await readSession(api);
    setUserState(me);
    setStatus(next);
    return me;
  }, [api]);

  useEffect(() => {
    void loadMe();
    // The client clears its tokens when a refresh fails; follow it.
    return api.onTokensChanged((tokens) => {
      if (!tokens) {
        setUserState(null);
        setStatus('anonymous');
      } else if (statusRef.current !== 'authenticated') {
        // Tokens appeared from outside login/register — the SSO callback page stores them
        // directly in the client. Without this, the route guard still sees 'anonymous' and
        // bounces the freshly signed-in user back to /login.
        setStatus('loading');
        void loadMe();
      }
    });
  }, [api, loadMe]);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      async login(body) {
        const res = await api.auth.login(body);
        setUserState(res.user);
        setStatus('authenticated');
        return res.user;
      },
      async register(body) {
        const res = await api.auth.register(body);
        setUserState(res.user);
        setStatus('authenticated');
        return res.user;
      },
      async demo(lang) {
        const res = await api.auth.demo({ lang });
        setUserState(res.user);
        setStatus('authenticated');
        // /demo is reachable while signed in (login and register are not), so whatever the previous account
        // had cached goes — nobody may see a stranger's numbers through the demo.
        queryClient.clear();
        return res.user;
      },
      async logout() {
        await api.auth.logout();
        setUserState(null);
        setStatus('anonymous');
        queryClient.clear();
      },
      reload: loadMe,
      setUser: (next) => setUserState(next),
    }),
    [api, loadMe, queryClient, status, user],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}

export function useAuth(): AuthContextValue {
  const ctx = use(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
